import React, { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CircularProgress from "@mui/material/CircularProgress";
import "./partSearchTab.css";
import {
  fetchAllActiveAttributes,
  fetchAllActiveDivisions,
  fetchAllActiveRestrictions,
  fetchAttributesByItemId,
  fetchSearchParts,
} from "../../graphql/partsCatalogQueries";
import {
  AVAILABILITY_OPTIONS,
  availabilityTooltipForCode,
  cellText,
  formatPartAttributes,
  formatPartRestrictions,
} from "../../utils/partCatalogFormatters";
import PartDetailsModal from "./PartDetailsModal";

const ATTR_HOVER_DEBOUNCE_MS = 150;
/** Delay closing when leaving the cell so the pointer can move into the popover to scroll. */
const ATTR_POPOVER_CLOSE_DELAY_MS = 200;

const emptyForm = () => ({
  div: "",
  pls: "",
  partNumber: "",
  itemId: "",
  description: "",
  availability: "",
  attribute: "",
  restriction: "",
  partAddDate: "",
});

/** Map Part Search form → `searchParts` GraphQL variables (empty → null). */
function searchPartsVariablesFromForm(f) {
  const s = (v) => {
    if (v == null) return null;
    const t = String(v).trim();
    return t === "" ? null : t;
  };
  return {
    div: s(f.div),
    pls: s(f.pls),
    part: s(f.partNumber),
    itemId: s(f.itemId),
    description: s(f.description),
    partAddDate: s(f.partAddDate),
    availability: s(f.availability),
    restriction: s(f.restriction),
    attribute: s(f.attribute),
  };
}

/** Form keys that count as search criteria (non-empty after trim). */
const SEARCH_CRITERIA_FIELD_KEYS = [
  "div",
  "pls",
  "partNumber",
  "itemId",
  "description",
  "availability",
  "attribute",
  "restriction",
  "partAddDate",
];

function getFilledSearchCriteriaKeys(f) {
  return SEARCH_CRITERIA_FIELD_KEYS.filter((key) => {
    const v = f[key];
    if (v == null) return false;
    return String(v).trim() !== "";
  });
}

/**
 * Search is allowed if: (1) at least two criteria are filled, or
 * (2) exactly one criterion is filled and it is only Part Number or only Item ID.
 */
function meetsPartSearchCriteria(f) {
  const keys = getFilledSearchCriteriaKeys(f);
  const n = keys.length;
  if (n === 0) return false;
  if (n >= 2) return true;
  const only = keys[0];
  return only === "partNumber" || only === "itemId";
}

function escapeCsvCell(val) {
  const s = val == null ? "" : String(val);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildPartSearchResultsCsv(rows) {
  const header = [
    "DIV",
    "PLS",
    "Part Number",
    "Item ID",
    "Part Name",
    "Availability",
    "Attribute",
    "Restriction",
    "Part Add Date",
  ];
  const lines = [header.map(escapeCsvCell).join(",")];
  for (const row of rows) {
    const itemIdStr =
      row.itemId != null && String(row.itemId).trim() !== ""
        ? String(row.itemId).trim()
        : "";
    const availCode = row.availability?.code;
    const availCodeStr =
      availCode != null && String(availCode).trim() !== ""
        ? String(availCode).trim()
        : "";
    lines.push(
      [
        cellText(row.div),
        cellText(row.pls),
        cellText(row.partNumber),
        itemIdStr || "-",
        cellText(row.partName),
        availCodeStr || "-",
        formatPartAttributes(row.attributes),
        formatPartRestrictions(row.restrictions),
        cellText(row.partAddDate),
      ]
        .map(escapeCsvCell)
        .join(",")
    );
  }
  return lines.join("\r\n");
}

function downloadCsvFile(filename, csvContent) {
  const blob = new Blob([`\uFEFF${csvContent}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function PartSearchTab() {
  const [detailsRow, setDetailsRow] = useState(null);
  const [attrHoverPopover, setAttrHoverPopover] = useState(null);
  const attrHoverCacheRef = useRef(new Map());
  const attrHoverTimeoutRef = useRef(null);
  const attrPopoverLeaveTimerRef = useRef(null);
  const currentHoverItemIdRef = useRef("");
  const [criteriaOpen, setCriteriaOpen] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [resultRows, setResultRows] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [divisionsError, setDivisionsError] = useState(null);
  const [divisionsLoading, setDivisionsLoading] = useState(false);
  const [divisionsFetchAttempted, setDivisionsFetchAttempted] = useState(false);
  const divisionsInFlightRef = useRef(false);
  const divisionsLoadedOkRef = useRef(false);
  const [restrictions, setRestrictions] = useState([]);
  const [restrictionsError, setRestrictionsError] = useState(null);
  const [restrictionsLoading, setRestrictionsLoading] = useState(false);
  const [restrictionsFetchAttempted, setRestrictionsFetchAttempted] =
    useState(false);
  const restrictionsInFlightRef = useRef(false);
  const restrictionsLoadedOkRef = useRef(false);
  const [attributes, setAttributes] = useState([]);
  const [attributesError, setAttributesError] = useState(null);
  const [attributesLoading, setAttributesLoading] = useState(false);
  const [attributesFetchAttempted, setAttributesFetchAttempted] =
    useState(false);
  const attributesInFlightRef = useRef(false);
  const attributesLoadedOkRef = useRef(false);
  const dateInputRef = useRef(null);

  /** GraphQL `allActiveDivisions` runs when the user focuses the DIV dropdown (Part Search). */
  const loadDivisionsFromDropdown = useCallback(async () => {
    if (divisionsInFlightRef.current) return;
    if (divisionsLoadedOkRef.current) return;

    divisionsInFlightRef.current = true;
    setDivisionsLoading(true);
    setDivisionsError(null);
    try {
      const list = await fetchAllActiveDivisions();
      setDivisions(list);
      divisionsLoadedOkRef.current = true;
    } catch (e) {
      setDivisions([]);
      setDivisionsError(e?.message || "Failed to load divisions");
      console.error(e);
    } finally {
      divisionsInFlightRef.current = false;
      setDivisionsLoading(false);
      setDivisionsFetchAttempted(true);
    }
  }, []);

  /** GraphQL `allActiveRestrictions` when the user focuses the Restriction dropdown. */
  const loadRestrictionsFromDropdown = useCallback(async () => {
    if (restrictionsInFlightRef.current) return;
    if (restrictionsLoadedOkRef.current) return;

    restrictionsInFlightRef.current = true;
    setRestrictionsLoading(true);
    setRestrictionsError(null);
    try {
      const list = await fetchAllActiveRestrictions();
      setRestrictions(list);
      restrictionsLoadedOkRef.current = true;
    } catch (e) {
      setRestrictions([]);
      setRestrictionsError(e?.message || "Failed to load restrictions");
      console.error(e);
    } finally {
      restrictionsInFlightRef.current = false;
      setRestrictionsLoading(false);
      setRestrictionsFetchAttempted(true);
    }
  }, []);

  /** GraphQL `allActiveAttributes` when the user focuses the Attribute dropdown. */
  const loadAttributesFromDropdown = useCallback(async () => {
    if (attributesInFlightRef.current) return;
    if (attributesLoadedOkRef.current) return;

    attributesInFlightRef.current = true;
    setAttributesLoading(true);
    setAttributesError(null);
    try {
      const list = await fetchAllActiveAttributes();
      setAttributes(list);
      attributesLoadedOkRef.current = true;
    } catch (e) {
      setAttributes([]);
      setAttributesError(e?.message || "Failed to load attributes");
      console.error(e);
    } finally {
      attributesInFlightRef.current = false;
      setAttributesLoading(false);
      setAttributesFetchAttempted(true);
    }
  }, []);

  /** Load restrictions and attributes when Part Search criteria is visible. */
  useEffect(() => {
    if (!criteriaOpen) return;
    loadRestrictionsFromDropdown();
    loadAttributesFromDropdown();
  }, [
    criteriaOpen,
    loadRestrictionsFromDropdown,
    loadAttributesFromDropdown,
  ]);

  useEffect(() => {
    return () => {
      clearTimeout(attrHoverTimeoutRef.current);
      clearTimeout(attrPopoverLeaveTimerRef.current);
    };
  }, []);

  const handleAttributeCellMouseEnter = useCallback((e, itemId) => {
    const id = itemId != null ? String(itemId).trim() : "";
    if (!id) return;
    clearTimeout(attrHoverTimeoutRef.current);
    clearTimeout(attrPopoverLeaveTimerRef.current);
    currentHoverItemIdRef.current = id;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = { top: rect.bottom + 6, left: rect.left };

    const cached = attrHoverCacheRef.current.get(id);
    if (cached) {
      setAttrHoverPopover({
        itemId: id,
        top: pos.top,
        left: pos.left,
        loading: false,
        items: cached,
        error: null,
      });
      return;
    }

    attrHoverTimeoutRef.current = setTimeout(async () => {
      if (currentHoverItemIdRef.current !== id) return;
      setAttrHoverPopover({
        itemId: id,
        top: pos.top,
        left: pos.left,
        loading: true,
        items: [],
        error: null,
      });
      try {
        const items = await fetchAttributesByItemId(id);
        if (currentHoverItemIdRef.current !== id) return;
        attrHoverCacheRef.current.set(id, items);
        setAttrHoverPopover((p) =>
          p?.itemId === id
            ? { ...p, loading: false, items, error: null }
            : p
        );
      } catch (err) {
        console.error(err);
        if (currentHoverItemIdRef.current !== id) return;
        setAttrHoverPopover((p) =>
          p?.itemId === id
            ? {
                ...p,
                loading: false,
                items: [],
                error: err?.message || "Failed to load attributes",
              }
            : p
        );
      }
    }, ATTR_HOVER_DEBOUNCE_MS);
  }, []);

  const closeAttrHoverPopover = useCallback(() => {
    clearTimeout(attrPopoverLeaveTimerRef.current);
    clearTimeout(attrHoverTimeoutRef.current);
    currentHoverItemIdRef.current = "";
    setAttrHoverPopover(null);
  }, []);

  const handleAttributeCellMouseLeave = useCallback(() => {
    attrPopoverLeaveTimerRef.current = setTimeout(() => {
      currentHoverItemIdRef.current = "";
      setAttrHoverPopover(null);
    }, ATTR_POPOVER_CLOSE_DELAY_MS);
  }, []);

  const handleAttrPopoverMouseEnter = useCallback(() => {
    clearTimeout(attrPopoverLeaveTimerRef.current);
  }, []);

  const handleAttrPopoverMouseLeave = useCallback(() => {
    closeAttrHoverPopover();
  }, [closeAttrHoverPopover]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleClear = () => {
    closeAttrHoverPopover();
    setForm(emptyForm());
    setResultRows([]);
    setSearchError(null);
  };

  const handleExportPartResults = () => {
    if (searchLoading || resultRows.length === 0) return;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const csv = buildPartSearchResultsCsv(resultRows);
    downloadCsvFile(`part-search-results-${stamp}.csv`, csv);
  };

  const handleSearch = async () => {
    setSearchError(null);
    if (!meetsPartSearchCriteria(form)) {
      setSearchError(
        "Enter at least two search criteria together, or search with only Part Number, or only Item ID."
      );
      return;
    }
    setSearchLoading(true);
    setResultRows([]);
    try {
      const rows = await fetchSearchParts(searchPartsVariablesFromForm(form));
      setResultRows(rows);
    } catch (e) {
      setResultRows([]);
      setSearchError(e?.message || "Search failed");
      console.error(e);
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <>
    <div className="partSearchTab">
      <section className="pscSection pscCriteriaSection">
        <button
          type="button"
          className="pscSectionHeader"
          onClick={() => setCriteriaOpen((o) => !o)}
          aria-expanded={criteriaOpen}
        >
          <span className="pscSectionTitle">Part Search Criteria</span>
          <span className="pscSectionToggle" aria-hidden>
            {criteriaOpen ? (
              <RemoveIcon fontSize="small" />
            ) : (
              <AddIcon fontSize="small" />
            )}
          </span>
        </button>
        {criteriaOpen && (
          <div className="pscSectionBody">
            <div className="pscCriteriaLayout">
              <div className="pscFieldGrid">
                <div className="pscField">
                  <label htmlFor="psc-div">DIV</label>
                  <select
                    id="psc-div"
                    className="pscInput"
                    value={form.div}
                    onChange={(e) => setField("div", e.target.value)}
                    onFocus={loadDivisionsFromDropdown}
                    disabled={divisionsLoading}
                    aria-busy={divisionsLoading}
                    title={divisionsError || undefined}
                  >
                    <option value="">
                      {divisionsLoading
                        ? "Loading divisions…"
                        : divisionsError
                          ? "-- Error — focus to retry --"
                          : divisionsFetchAttempted
                            ? "-- Select --"
                            : "-- Select — click to load divisions --"}
                    </option>
                    {divisions.map((d) => {
                      const idStr = String(d.id ?? "");
                      const nameStr = d.name != null && d.name !== "" ? String(d.name) : "";
                      const label =
                        idStr && nameStr
                          ? `${idStr}, ${nameStr}`
                          : idStr || nameStr || "—";
                      return (
                        <option key={idStr || label} value={idStr}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="pscField">
                  <label htmlFor="psc-pls">PLS</label>
                  <input
                    id="psc-pls"
                    className="pscInput"
                    type="text"
                    value={form.pls}
                    onChange={(e) => setField("pls", e.target.value)}
                  />
                </div>
                <div className="pscField">
                  <label htmlFor="psc-partNumber">Part Number</label>
                  <input
                    id="psc-partNumber"
                    className="pscInput"
                    type="text"
                    value={form.partNumber}
                    onChange={(e) => setField("partNumber", e.target.value)}
                  />
                </div>
                <div className="pscField">
                  <label htmlFor="psc-itemId">Item ID</label>
                  <input
                    id="psc-itemId"
                    className="pscInput"
                    type="text"
                    value={form.itemId}
                    onChange={(e) => setField("itemId", e.target.value)}
                  />
                </div>
                <div className="pscField">
                  <label htmlFor="psc-description">Description</label>
                  <input
                    id="psc-description"
                    className="pscInput"
                    type="text"
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                  />
                </div>
                <div className="pscField">
                  <label htmlFor="psc-availability">Availability</label>
                  <select
                    id="psc-availability"
                    className="pscInput"
                    value={form.availability}
                    onChange={(e) => setField("availability", e.target.value)}
                    title={
                      AVAILABILITY_OPTIONS.find(
                        (o) => o.value === form.availability
                      )?.title
                    }
                  >
                    <option value="">-- Select --</option>
                    {AVAILABILITY_OPTIONS.map(({ value, title }) => (
                      <option key={value} value={value} title={title}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="pscField">
                  <label htmlFor="psc-attribute">Attribute</label>
                  <select
                    id="psc-attribute"
                    className="pscInput"
                    value={form.attribute}
                    onChange={(e) => setField("attribute", e.target.value)}
                    onFocus={loadAttributesFromDropdown}
                    disabled={attributesLoading}
                    aria-busy={attributesLoading}
                    title={attributesError || undefined}
                  >
                    <option value="">
                      {attributesLoading
                        ? "Loading attributes…"
                        : attributesError
                          ? "-- Error — focus to retry --"
                          : attributesFetchAttempted
                            ? "-- Select --"
                            : "-- Select — click to load attributes --"}
                    </option>
                    {attributes.map((a) => {
                      const idStr = String(a.id ?? "");
                      const nameStr =
                        a.name != null && a.name !== "" ? String(a.name) : "";
                      const label =
                        idStr && nameStr
                          ? `${idStr}, ${nameStr}`
                          : idStr || nameStr || "—";
                      return (
                        <option key={idStr || label} value={idStr}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="pscField">
                  <label htmlFor="psc-restriction">Restriction</label>
                  <select
                    id="psc-restriction"
                    className="pscInput"
                    value={form.restriction}
                    onChange={(e) => setField("restriction", e.target.value)}
                    onFocus={loadRestrictionsFromDropdown}
                    disabled={restrictionsLoading}
                    aria-busy={restrictionsLoading}
                    title={restrictionsError || undefined}
                  >
                    <option value="">
                      {restrictionsLoading
                        ? "Loading restrictions…"
                        : restrictionsError
                          ? "-- Error — focus to retry --"
                          : restrictionsFetchAttempted
                            ? "-- Select --"
                            : "-- Select — click to load restrictions --"}
                    </option>
                    {restrictions.map((r) => {
                      const idStr = String(r.id ?? "");
                      const nameStr =
                        r.name != null && r.name !== "" ? String(r.name) : "";
                      const label =
                        idStr && nameStr
                          ? `${idStr}, ${nameStr}`
                          : idStr || nameStr || "—";
                      return (
                        <option key={idStr || label} value={idStr}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="pscField pscFieldDate">
                  <label htmlFor="psc-partAddDate">Part Add Date</label>
                  <div className="pscDateRow">
                    <input
                      ref={dateInputRef}
                      id="psc-partAddDate"
                      className="pscInput pscDateInput"
                      type="date"
                      value={form.partAddDate}
                      onChange={(e) => setField("partAddDate", e.target.value)}
                    />
                    <button
                      type="button"
                      className="pscCalendarBtn"
                      onClick={() => dateInputRef.current?.showPicker?.() || dateInputRef.current?.click()}
                      aria-label="Open date picker"
                    >
                      <CalendarMonthIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="pscActions">
                <button
                  type="button"
                  className="pscBtnPrimary pscBtnPrimaryWithSpinner"
                  onClick={handleSearch}
                  disabled={searchLoading}
                  aria-busy={searchLoading}
                >
                  {searchLoading ? (
                    <>
                      <CircularProgress
                        className="pscBtnSpinner"
                        color="inherit"
                        size={20}
                        thickness={4}
                        aria-hidden
                      />
                      <span>Searching…</span>
                    </>
                  ) : (
                    "Search"
                  )}
                </button>
                <button
                  type="button"
                  className="pscBtnPrimary"
                  onClick={handleClear}
                  disabled={searchLoading}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="pscSection pscResultSection">
        <div className="pscSectionHeader pscSectionHeaderStatic">
          <span className="pscSectionTitle">Part Search Result</span>
          <button
            type="button"
            className="pscExportBtn"
            onClick={handleExportPartResults}
            disabled={searchLoading || resultRows.length === 0}
            title="Download results as CSV"
          >
            Export
          </button>
        </div>
        <div
          className={
            resultRows.length > 0 && !searchLoading
              ? "pscSectionBody pscResultBody pscResultBodyTable"
              : "pscSectionBody pscResultBody"
          }
        >
          {searchError ? (
            <p className="pscSearchError" role="alert">
              {searchError}
            </p>
          ) : searchLoading ? (
            <div className="pscSearchLoading" aria-live="polite">
              <CircularProgress size={40} thickness={4} aria-label="Searching" />
              <span className="pscSearchLoadingLabel">Searching…</span>
            </div>
          ) : resultRows.length === 0 ? (
            <p className="pscNoData">No Data Found</p>
          ) : (
            <div className="pscTableWrap">
              <table className="pscTable pscPartSearchResultTable">
                <thead>
                  <tr>
                    <th>DIV</th>
                    <th>PLS</th>
                    <th>Part Number</th>
                    <th>Item ID</th>
                    <th>Part Name</th>
                    <th>Availability</th>
                    <th>Attribute</th>
                    <th>Restriction</th>
                    <th>Part Add Date</th>
                  </tr>
                </thead>
                <tbody>
                  {resultRows.map((row, idx) => {
                    const itemIdStr =
                      row.itemId != null && String(row.itemId).trim() !== ""
                        ? String(row.itemId).trim()
                        : "";
                    const availCode = row.availability?.code;
                    const availCodeStr =
                      availCode != null && String(availCode).trim() !== ""
                        ? String(availCode).trim()
                        : "";
                    return (
                      <tr
                        key={`${row.partNumber ?? ""}-${row.itemId ?? ""}-${idx}`}
                      >
                        <td>{cellText(row.div)}</td>
                        <td>{cellText(row.pls)}</td>
                        <td>{cellText(row.partNumber)}</td>
                        <td>
                          {itemIdStr ? (
                            <button
                              type="button"
                              className="pscModelLink"
                              onClick={() => setDetailsRow(row)}
                            >
                              {itemIdStr}
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>{cellText(row.partName)}</td>
                        <td className="pscAvailabilityCell">
                          {availCodeStr ? (
                            <span
                              title={availabilityTooltipForCode(availCodeStr)}
                            >
                              {availCodeStr}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td
                          className="pscAttrCell"
                          onMouseEnter={(e) =>
                            handleAttributeCellMouseEnter(e, itemIdStr)
                          }
                          onMouseLeave={handleAttributeCellMouseLeave}
                        >
                          {formatPartAttributes(row.attributes)}
                        </td>
                        <td>{formatPartRestrictions(row.restrictions)}</td>
                        <td>{cellText(row.partAddDate)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
    <PartDetailsModal
      open={detailsRow != null}
      snapshotRow={detailsRow}
      onClose={() => setDetailsRow(null)}
    />
    {attrHoverPopover &&
      createPortal(
        <div
          className="pscAttrHoverPopover"
          style={{
            top: attrHoverPopover.top,
            left: attrHoverPopover.left,
          }}
          role="dialog"
          aria-label="Attributes"
          onMouseEnter={handleAttrPopoverMouseEnter}
          onMouseLeave={handleAttrPopoverMouseLeave}
        >
          {attrHoverPopover.loading ? (
            <div className="pscAttrHoverPopoverLoading">
              <CircularProgress size={22} thickness={4} />
              <span>Loading…</span>
            </div>
          ) : attrHoverPopover.error ? (
            <p className="pscAttrHoverPopoverError">{attrHoverPopover.error}</p>
          ) : attrHoverPopover.items.length === 0 ? (
            <p className="pscAttrHoverPopoverEmpty">No attributes returned.</p>
          ) : (
            <ul className="pscAttrHoverPopoverList">
              {attrHoverPopover.items.map((a, i) => (
                <li key={`${a.id ?? ""}-${a.name ?? ""}-${i}`}>
                  <span className="pscAttrHoverId">{cellText(a.id)}</span>
                  <span className="pscAttrHoverName">{cellText(a.name)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
